import React, { FormEvent, useState } from 'react';
import { Calendar, Loader2 } from 'lucide-react';
import { DepotLayout } from './DepotLayout';
import type { DepotBrandingDto } from '../types';

interface Props {
  firstName?: string;
  /** Date d'expiration ISO renvoyée par /etat — informative. */
  expiresAt?: string;
  /** Vrai pendant l'appel à /authentifier. */
  isSubmitting: boolean;
  /**
   * Message d'erreur métier renvoyé par le back (ex: "Date de naissance
   * incorrecte. Il vous reste 2 tentative(s).").
   */
  errorMessage?: string;
  /** Soumission — la DDN est au format ISO yyyy-MM-dd. */
  onSubmit: (dateNaissance: string) => void;
  /** Habillage courtier/produit renvoyé par /etat. */
  branding?: DepotBrandingDto;
}

/**
 * Première étape côté souscripteur : on lui demande sa date de naissance
 * pour vérifier qu'il est bien le destinataire du lien.
 *
 * Le back compte les tentatives — 3 échecs et le token est bloqué. On affiche
 * le message back tel quel (il contient le nombre de tentatives restantes).
 */
export const DepotDdnForm: React.FC<Props> = ({
  firstName,
  expiresAt,
  isSubmitting,
  errorMessage,
  onSubmit,
  branding
}) => {
  const [dateNaissance, setDateNaissance] = useState('');

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!dateNaissance || isSubmitting) return;
    onSubmit(dateNaissance);
  };

  const formattedExpiry = expiresAt
    ? new Date(expiresAt).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
    : null;

  return (
    <DepotLayout branding={branding}>
      <div className="flex flex-col gap-6">
        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/10 mb-4">
            <Calendar className="w-7 h-7 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-gray-800">
            {firstName ? `Bonjour ${firstName} 👋` : 'Bonjour 👋'}
          </h1>
          <p className="mt-2 text-gray-600 leading-relaxed">
            Pour accéder au dépôt sécurisé de vos documents médicaux, veuillez
            confirmer votre date de naissance.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <label className="flex flex-col gap-1.5">
            <span className="text-sm font-medium text-gray-700">
              Date de naissance
            </span>
            <input
              type="date"
              required
              autoFocus
              disabled={isSubmitting}
              value={dateNaissance}
              onChange={(e) => setDateNaissance(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2.5 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-gray-50 disabled:text-gray-500"
            />
          </label>

          {errorMessage && (
            <div
              role="alert"
              className="rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
            >
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            disabled={!dateNaissance || isSubmitting}
            className="inline-flex items-center justify-center gap-2 w-full rounded-lg bg-primary px-4 py-3 text-white font-medium hover:bg-primary/90 transition disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isSubmitting ? 'Vérification…' : 'Continuer'}
          </button>

          {formattedExpiry && (
            <p className="text-xs text-center text-gray-400">
              Lien valable jusqu'au {formattedExpiry}.
            </p>
          )}
        </form>
      </div>
    </DepotLayout>
  );
};
